import { z } from 'zod';

// ---- M13: kereskedelmi útvonalak, blokád, megaprojektek, késői nyelők ----

export const TRADE_RULES = {
  /** Útvonal-hossz technológia nélkül (csillagközi ugrás). */
  baseRange: 3,
  /** Az útvonal hozama óránként az 1 ugrásos útvonalra; hosszabb útvonal többet hoz. */
  baseYieldPerHour: 40,
  perExtraHop: 0.15,
  /** Az útvonal fenntartása a tulajdonosnak (fém/óra). */
  upkeepMetalPerHour: 4,
} as const;

/** Az útvonal hozama (a technológiai szorzó előtt). */
export function routeYieldPerHour(hops: number): number {
  return Math.round(TRADE_RULES.baseYieldPerHour * (1 + TRADE_RULES.perExtraHop * Math.max(0, hops - 1)));
}

/** Birodalomméret szerinti közigazgatási teher: a bányák/energiaállomások kitermelésének hányada. */
export const ADMIN_DRAG = { freeSystems: 10, perSystem: 0.012, max: 0.45 } as const;
export function adminDragRate(systems: number): number {
  return Math.min(ADMIN_DRAG.max, ADMIN_DRAG.perSystem * Math.max(0, systems - ADMIN_DRAG.freeSystems));
}

/** Ellátási keret: a fölötte lévő hadihajók fenntartása hajónként ennyivel drágább. */
export const SUPPLY = { base: 8, perSystem: 1, overSurcharge: 0.05 } as const;
export function supplyCap(systems: number, techCap: number): number {
  return SUPPLY.base + SUPPLY.perSystem * systems + techCap;
}
export function fleetUpkeepMultiplier(ships: number, cap: number): number {
  return 1 + SUPPLY.overSurcharge * Math.max(0, ships - cap);
}

export type MegaprojectKind = 'stellar_forge' | 'dyson_array' | 'orbital_vault' | 'logistics_hub';
export const MEGAPROJECT_KINDS: readonly MegaprojectKind[] = ['stellar_forge', 'dyson_array', 'orbital_vault', 'logistics_hub'];
export const MEGAPROJECT_MAX_LEVEL = 3;

export interface MegaprojectDef {
  name: string;
  description: string;
  /** Szintenkénti hatás. */
  perLevel: { miningOutput?: number; energyOutput?: number; storage?: number; fleetUpkeep?: number };
  /** Költség rendszerenként (a birodalom méretével nő, így a nagy birodalom nagy nyelőt kap). */
  metalPerSystem: number;
  energyPerSystem: number;
  baseTimeMs: number;
}

export const MEGAPROJECTS: Record<MegaprojectKind, MegaprojectDef> = {
  stellar_forge: { name: 'Csillagkohó', description: 'A bányák termelése szintenként +10%.', perLevel: { miningOutput: 0.1 }, metalPerSystem: 700, energyPerSystem: 350, baseTimeMs: 4 * 3_600_000 },
  dyson_array: { name: 'Dyson-gyűrű', description: 'Az energiaállomások termelése szintenként +12%.', perLevel: { energyOutput: 0.12 }, metalPerSystem: 600, energyPerSystem: 400, baseTimeMs: 4 * 3_600_000 },
  orbital_vault: { name: 'Orbitális raktár', description: 'A raktárkapacitás szintenként +25%.', perLevel: { storage: 0.25 }, metalPerSystem: 500, energyPerSystem: 250, baseTimeMs: 3 * 3_600_000 },
  logistics_hub: { name: 'Logisztikai csomópont', description: 'A flotta fenntartása szintenként −8%.', perLevel: { fleetUpkeep: 0.08 }, metalPerSystem: 450, energyPerSystem: 300, baseTimeMs: 3 * 3_600_000 },
};

/** A megaprojekt ára és ideje: a szint és a birodalom mérete szerint nő. */
export function megaprojectCost(kind: MegaprojectKind, level: number, systems: number): { metal: number; energy: number; timeMs: number } {
  const d = MEGAPROJECTS[kind];
  const scale = Math.max(8, systems) * (1 + 0.6 * (level - 1));
  return {
    metal: Math.round(d.metalPerSystem * scale / 50) * 50,
    energy: Math.round(d.energyPerSystem * scale / 50) * 50,
    timeMs: Math.round(d.baseTimeMs * (1 + 0.5 * (level - 1))),
  };
}

/** Karbantartás: a költség 0,4%-a óránként fémben (kész projekt). */
export const MEGAPROJECT_UPKEEP_RATE = 0.004;

export interface MegaprojectEffects { miningOutput: number; energyOutput: number; storage: number; fleetUpkeep: number }
export function megaprojectEffects(done: Array<{ kind: string; level: number }>): MegaprojectEffects {
  const e: MegaprojectEffects = { miningOutput: 0, energyOutput: 0, storage: 0, fleetUpkeep: 0 };
  for (const p of done) {
    const d = MEGAPROJECTS[p.kind as MegaprojectKind];
    if (!d) continue;
    e.miningOutput += d.perLevel.miningOutput ?? 0;
    e.energyOutput += d.perLevel.energyOutput ?? 0;
    e.storage += d.perLevel.storage ?? 0;
    e.fleetUpkeep += d.perLevel.fleetUpkeep ?? 0;
  }
  return e;
}

// ---- API sémák ----

export const TradeRouteViewSchema = z.object({
  id: z.string().uuid(),
  role: z.enum(['owner', 'partner']),
  partnerCivId: z.string().uuid(),
  partnerCivName: z.string(),
  fromSystemId: z.string().uuid(),
  fromSystemName: z.string(),
  toSystemId: z.string().uuid(),
  toSystemName: z.string(),
  exportResource: z.enum(['metal', 'energy']),
  hops: z.number().int(),
  /** Mit kap a nézet tulajdonosa óránként (a blokád nélküli érték). */
  incomePerHour: z.object({ metal: z.number().int(), energy: z.number().int() }),
  status: z.enum(['active', 'blockaded']),
  blockadedByCivName: z.string().nullable(),
  path: z.array(z.string()),
});
export type TradeRouteView = z.infer<typeof TradeRouteViewSchema>;

export const CreateTradeRouteSchema = z.object({
  partnerCivId: z.string().uuid(),
  fromSystemId: z.string().uuid(),
  toSystemId: z.string().uuid(),
  exportResource: z.enum(['metal', 'energy']),
  idempotencyKey: z.string().min(1).max(128),
});
export type CreateTradeRoute = z.infer<typeof CreateTradeRouteSchema>;

export const MegaprojectViewSchema = z.object({
  kind: z.enum(['stellar_forge', 'dyson_array', 'orbital_vault', 'logistics_hub']),
  name: z.string(),
  description: z.string(),
  doneLevel: z.number().int(),
  building: z.object({ level: z.number().int(), completesAt: z.string().datetime() }).nullable(),
  next: z.object({ level: z.number().int(), metal: z.number().int(), energy: z.number().int(), timeMs: z.number().int() }).nullable(),
});
export type MegaprojectView = z.infer<typeof MegaprojectViewSchema>;

export const StartMegaprojectSchema = z.object({
  kind: z.enum(['stellar_forge', 'dyson_array', 'orbital_vault', 'logistics_hub']),
  idempotencyKey: z.string().min(1).max(128),
});
export type StartMegaproject = z.infer<typeof StartMegaprojectSchema>;
